/**
 * `CreditsList` — per-credit-revoke (2026-10-29) and, the same day,
 * membership-per-credit-revoke. The list behind a package row's "Sessions
 * (left / total)" cell and a membership row's "Sessions (left / cycle)" cell
 * on the member page: ONE ROW PER CREDIT, so staff see which credits a
 * session used, which went by transfer (packages only), which the team took
 * off (and why), and which are still there — and can cross a still-available
 * one out (✕ → a reason the client's assistant will quote) or put a
 * crossed-out one back (Restore).
 *
 * The rows arrive composed from the backend (`MemberDetailPackage.credits`
 * and `MemberDetailMembership.credits`, built by `buildPackageCreditRows` and
 * `buildMembershipCreditRows` in the member-detail repository), so the
 * arithmetic — and the "which cancel kept its credit" verdict, shared with
 * the Past sessions badge — has ONE home. This file only renders and
 * dispatches, through `ActionModalButton`, and is the single
 * `revoke_package_credit` / `restore_package_credit` /
 * `revoke_membership_credit` / `restore_membership_credit` site in the SPA
 * (pinned by `CreditsList.test.ts`).
 *
 * Rendered without a portal so the test can `renderToStaticMarkup` it; the
 * member page wraps it in its `MetaModal`.
 */

import type { JSX } from "react";
import { ActionModalButton } from "./ActionModalButton";
import type {
  MemberDetailMembership,
  MemberDetailMembershipCredit,
  MemberDetailPackage,
  MemberDetailPackageCredit,
  MemberDetailRestoredCredit,
} from "../lib/api";

type Credit = MemberDetailPackageCredit | MemberDetailMembershipCredit;

function fmtDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** `human:staff:<id>…` → "staff"; `system:cli…` → "the CLI"; anything else verbatim. */
function actorLabel(actor: string): string {
  if (actor.startsWith("human:staff")) return "staff";
  if (actor.startsWith("system:cli")) return "the CLI";
  return actor;
}

function kindLabel(c: Credit): string {
  switch (c.kind) {
    case "used":
      if (c.kept_on_cancel) return "Kept on cancel";
      if (c.attendance_status === "no_show") return "No-show";
      if (c.status === "active") return "Booked";
      return "Used";
    case "transferred":
      return "Transferred";
    case "revoked":
      return "Taken off";
    case "adjusted":
      return "Used";
    case "removed":
      return "Removed";
    case "available":
      return "Available";
  }
}

function detail(c: Credit): string {
  switch (c.kind) {
    case "used":
      return `${fmtWhen(c.starts_at)}${c.trainer_name !== null ? ` with ${c.trainer_name}` : ""}`;
    case "transferred":
      return `to ${c.to_member_name ?? "another member"}`;
    case "revoked":
      return `${fmtDay(c.revoked_at)} — ${c.reason !== null ? `“${c.reason}”` : "reason removed"} · by ${actorLabel(c.revoked_by_actor)}`;
    case "adjusted":
      return "no booking on record (taken before per-credit records, or a late-change penalty)";
    case "removed":
      return "went with the package when it was revoked whole";
    case "available":
      return "";
  }
}

function rowKey(c: Credit, i: number): string {
  return c.kind === "used"
    ? `b-${c.booking_id}`
    : c.kind === "revoked"
      ? `r-${c.revocation_id}`
      : `${c.kind}-${i}`;
}

/**
 * The shared table: one row per credit, ✕ on an available one, Restore on a
 * taken-off one, the restored history underneath. Which action a button
 * dispatches, and what it says, is the caller's — the package and the
 * membership differ only there.
 */
function CreditsTable({
  rows,
  restored,
  empty,
  editable,
  onChanged,
  revoke,
  restore,
}: {
  readonly rows: ReadonlyArray<Credit>;
  readonly restored: ReadonlyArray<MemberDetailRestoredCredit>;
  readonly empty: string;
  readonly editable: boolean;
  readonly onChanged: () => void;
  readonly revoke: {
    readonly action: string;
    readonly prefill: Readonly<Record<string, unknown>>;
    readonly description: string;
  };
  readonly restore: {
    readonly action: string;
    readonly description: (revokedAt: string) => string;
  };
}): JSX.Element {
  return (
    <>
      {rows.length === 0 ? (
        <p className="empty">{empty}</p>
      ) : (
        <table className="pkg-credits-list">
          <tbody>
            {rows.map((c, i) => (
              <tr key={rowKey(c, i)} className={`pkg-credit-row pkg-credit-${c.kind}`}>
                <td className="pkg-credit-n">{i + 1}</td>
                <td className="pkg-credit-kind">{kindLabel(c)}</td>
                <td className="pkg-credit-detail">{detail(c)}</td>
                <td className="pkg-credit-action">
                  {editable && c.kind === "available" ? (
                    <ActionModalButton
                      action={revoke.action}
                      buttonLabel="✕"
                      buttonClassName="row-action-btn row-action-btn-danger pkg-credit-x"
                      modalTitle="Take this credit off"
                      description={revoke.description}
                      submitLabel="Take credit off"
                      prefill={revoke.prefill}
                      fields={[
                        {
                          kind: "textarea",
                          name: "reason",
                          label: "Reason — the client's assistant can quote this",
                          required: true,
                          maxLength: 500,
                          placeholder:
                            "e.g. swapped for a free home session on 29 Oct",
                        },
                      ]}
                      onDone={onChanged}
                    />
                  ) : editable && c.kind === "revoked" ? (
                    <ActionModalButton
                      action={restore.action}
                      buttonLabel="Restore"
                      buttonClassName="row-action-btn"
                      modalTitle="Put this credit back"
                      description={restore.description(c.revoked_at)}
                      submitLabel="Put credit back"
                      prefill={{ revocation_id: c.revocation_id }}
                      fields={[
                        {
                          kind: "text",
                          name: "note",
                          label: "Note for the team (optional)",
                          maxLength: 500,
                        },
                      ]}
                      onDone={onChanged}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {restored.length > 0 ? (
        <ul className="pkg-credits-restored">
          {restored.map((r) => (
            <li key={r.revocation_id}>
              Put back {fmtDay(r.restored_at)} by {actorLabel(r.restored_by_actor)}
              {r.restore_note !== null ? ` — “${r.restore_note}”` : ""} (taken
              off {fmtDay(r.revoked_at)}
              {r.reason !== null ? `: “${r.reason}”` : ""})
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export function PackageCreditsList({
  pkg,
  editable,
  onChanged,
}: {
  readonly pkg: MemberDetailPackage;
  /** ✕ and Restore render only when the table has a refetch hook AND the package is active. */
  readonly editable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const total = pkg.total_sessions;
  const expired =
    pkg.expires_at !== null && Date.parse(pkg.expires_at) < Date.now();
  const summary =
    total === null
      ? `${pkg.remaining_sessions} left · size unknown`
      : `${pkg.remaining_sessions} of ${total} left`;
  return (
    <div className="pkg-credits">
      <p className="pkg-credits-summary">
        {summary}
        {pkg.over_recorded_credits > 0
          ? ` · ${pkg.over_recorded_credits} more recorded than the pack size (an opening balance from the old system)`
          : ""}
      </p>
      <CreditsTable
        rows={pkg.credits}
        restored={pkg.restored_credits}
        empty="No credits recorded on this package."
        editable={editable}
        onChanged={onChanged}
        revoke={{
          action: "revoke_package_credit",
          prefill: { member_package_id: pkg.id },
          description: `Takes ONE credit off “${pkg.label}” (${pkg.remaining_sessions} left now). The reason is what the client's assistant will say if they ask where the credit went — write it for them.`,
        }}
        restore={{
          action: "restore_package_credit",
          description: (revokedAt) =>
            `Puts ONE credit back on “${pkg.label}” and marks the ${fmtDay(revokedAt)} removal as undone — the client's assistant will stop mentioning it.${expired ? " This package has expired: move its expiry afterwards if the client is to use the credit." : ""}`,
        }}
      />
    </div>
  );
}

/**
 * membership-per-credit-revoke — the same list over a membership's running
 * balance: the bookings it paid for since the cycle began, the credits it
 * gave away by transfer, the credits the team took off, what is left. No
 * size to reconcile against, so no
 * "adjusted" rows and no "of N" in the summary; an unlimited plan has no
 * list at all (the cell is not a trigger for one).
 */
export function MembershipCreditsList({
  membership,
  editable,
  onChanged,
}: {
  readonly membership: MemberDetailMembership;
  /** ✕ and Restore render only when the table has a refetch hook AND the membership is live. */
  readonly editable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const label = membership.membership_slug;
  if (membership.remaining_sessions === null) {
    return (
      <div className="pkg-credits">
        <p className="empty">An unlimited plan — there is no credit to list, take off or put back.</p>
      </div>
    );
  }
  const perCycle =
    membership.sessions_per_cycle !== null
      ? ` · ${membership.sessions_per_cycle} a cycle`
      : "";
  return (
    <div className="pkg-credits">
      <p className="pkg-credits-summary">
        {membership.remaining_sessions} left{perCycle}
        {membership.cycle_started_at !== null
          ? ` · sessions since ${fmtDay(membership.cycle_started_at)} (the balance carries over between cycles)`
          : ""}
      </p>
      <CreditsTable
        rows={membership.credits}
        restored={membership.restored_credits}
        empty="No credits recorded on this membership this cycle."
        editable={editable}
        onChanged={onChanged}
        revoke={{
          action: "revoke_membership_credit",
          prefill: { member_membership_id: membership.id },
          description: `Takes ONE credit off the “${label}” membership balance (${membership.remaining_sessions} left now). The reason is what the client's assistant will say if they ask where the credit went — write it for them.`,
        }}
        restore={{
          action: "restore_membership_credit",
          description: (revokedAt) =>
            `Puts ONE credit back on the “${label}” membership balance and marks the ${fmtDay(revokedAt)} removal as undone — the client's assistant will stop mentioning it.`,
        }}
      />
    </div>
  );
}
